import type { ListParams } from '@nucleus-commerce/admin-kit/index-table';
import {
  createHelpArticle,
  createHelpCategory,
  deleteHelpArticle,
  deleteHelpCategory,
  getStoreHelpArticle,
  listHelpArticleFeedback,
  listStoreHelpArticles,
  listStoreHelpCategories,
  updateHelpArticle,
  updateHelpCategory,
  type CreateHelpArticleRequest,
  type CreateHelpCategoryRequest,
  type HelpArticle,
  type HelpCategory,
  type ListHelpArticleFeedback200,
  type ListHelpArticleFeedbackParams,
  type ListStoreHelpArticles200,
  type UpdateHelpArticleRequest,
  type UpdateHelpCategoryRequest,
} from '@nucleus-commerce/api-client/admin';

/**
 * Article list params as `listParams` builds them from the route's search: `q`, `sort`, the flat
 * filter params (`status=draft`, `visibility=internal`, `categoryId=a,b`), `cursor`, `limit`.
 */
export type HelpArticleListParams = ListParams;

export const helpCenterApi = {
  listArticles: (
    storeId: string,
    params: HelpArticleListParams,
  ): Promise<ListStoreHelpArticles200> => listStoreHelpArticles({ storeId }, params),
  getArticle: (storeId: string, id: string): Promise<HelpArticle> =>
    getStoreHelpArticle({ storeId, id }),
  createArticle: (storeId: string, body: CreateHelpArticleRequest): Promise<HelpArticle> =>
    createHelpArticle({ storeId }, body),
  updateArticle: (
    storeId: string,
    id: string,
    body: UpdateHelpArticleRequest,
  ): Promise<HelpArticle> => updateHelpArticle({ storeId, id }, body),
  removeArticle: (storeId: string, id: string): Promise<void> => deleteHelpArticle({ storeId, id }),
  /** Votes and comments on one article, newest first. */
  listFeedback: (
    storeId: string,
    id: string,
    params: ListHelpArticleFeedbackParams,
  ): Promise<ListHelpArticleFeedback200> => listHelpArticleFeedback({ storeId, id }, params),

  /** Bounded (50 per store), ordered by position. */
  listCategories: (storeId: string): Promise<HelpCategory[]> =>
    listStoreHelpCategories({ storeId }).then((r) => r.items),
  createCategory: (storeId: string, body: CreateHelpCategoryRequest): Promise<HelpCategory> =>
    createHelpCategory({ storeId }, body),
  updateCategory: (
    storeId: string,
    id: string,
    body: UpdateHelpCategoryRequest,
  ): Promise<HelpCategory> => updateHelpCategory({ storeId, id }, body),
  removeCategory: (storeId: string, id: string): Promise<void> =>
    deleteHelpCategory({ storeId, id }),
};
